import React, { useMemo, useState } from 'react';
import { Button, message } from 'antd';
import OrderListTemplate from '../components/OrderListTemplate';
import { MergedOrder, OrderTabScene } from '../../../types/process/order';
import OrderTriggerActionMap, { OrderTriggerActionProvider, type OrderTriggerTarget } from '../components/OrderTriggerActionMap';
import { useDefaultQueryFields } from '../order.effect';
import { useMainBodyMap, useShopMap, useStaffMap } from '../../../services/store/organization.store';
import { mainBodyStoreUnionMultiSelect } from '../../../components/Business/QueryFieldPresets';
import TransferOrderModal from '../components/modals/TransferOrderModal';
import { OrderAuditInfo } from '../components/OrderAuditInfo';
import { Typography } from 'antd';

const { Text } = Typography;

const TeamAuditWorkbench: React.FC = () => {
  const tabScene = OrderTabScene.GroupTeamAudit;
  const baseQueryFields = useDefaultQueryFields(tabScene);
  const { mainBodyList } = useMainBodyMap();
  const { shopList } = useShopMap();
  const { staffMap } = useStaffMap();

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<MergedOrder[]>([]);
  const [isTransferModalVisible, setIsTransferModalVisible] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const queryFields = useMemo(() => {
    const fields = [...baseQueryFields];
    const storeIndex = fields.findIndex((f) => f.key === 'store_id');
    const storeField = mainBodyStoreUnionMultiSelect(mainBodyList, shopList, 'store_id');
    if (storeIndex >= 0) {
      fields[storeIndex] = { ...storeField, rememberKey: '/order/team-audit/store_id' };
    } else {
      fields.push({ ...storeField, rememberKey: '/order/team-audit/store_id' });
    }
    return fields;
  }, [baseQueryFields, mainBodyList, shopList]);

  const renderTableActions = (order: MergedOrder, context?: any) => {
    const sceneKey = context?.scene || tabScene;
    const targets: OrderTriggerTarget[] = ['reportView'];

    const o = order.order;
    const auditStatus = o.auditStatus;
    const decisionStatus = o.decisionStatus;

    if (sceneKey === 'tab_team_audit_all' || sceneKey === 'tab_team_audit_in_progress') {
      targets.push('supervisorForceAudit','auditView','transferOrder');

      if (decisionStatus === 1) { // 待补充材料
        targets.push('supplementAction');
      } else if (decisionStatus === 2) { // 待预收
        targets.push('prepayAction');
      } 
    } else {
      targets.push('auditView');
      if (sceneKey === 'tab_team_suspended') {
        targets.push('transferOrder', 'supervisorForceAudit');
      }
      if (sceneKey === 'tab_team_supplement_material') {
        targets.push('supplementAction', 'supervisorForceAudit');
      }
      if (sceneKey === 'tab_team_prepay_rent') {
        targets.push('prepayAction', 'supervisorForceAudit');
      }
      if (sceneKey === 'tab_team_pass_pending_delivery') {
        targets.push('transferOrder');
      }
    }

    return <OrderTriggerActionMap layout="list" targets={targets} order={order} forceAllowAuditToLock={true} />;
  };

  const renderInfo = (order: MergedOrder, context?: any) => {
    const sceneKey = context?.scene || tabScene;
    if (sceneKey === 'tab_team_audit_all') {
      return (
        <div style={{ marginTop: 8 }}>
          <OrderAuditInfo order={order} />
        </div>
      );
    }
    return null;
  };

  const handleBatchTransfer = (sceneKey: string) => {
    if (selectedRows.length === 0) {
      message.warning('请先选择要转单的订单');
      return;
    }
    setIsTransferModalVisible(true);
  };

  return (
    <OrderTriggerActionProvider onReload={() => setRefreshKey(k => k + 1)}>
      <OrderListTemplate
        refreshKey={refreshKey}
        tabScene={tabScene}
        defaultScene='tab_team_audit_all'
        queryFields={queryFields}
        tabStyleConfig={{
          importantTabs: ['tab_team_supplement_material', 'tab_team_prepay_rent'],
        }}
        batchActionNode={
          <Button type="primary" onClick={() => handleBatchTransfer('')} disabled={selectedRows.length === 0}>
            批量转单 ({selectedRows.length})
          </Button>
        }
        viewModes={[
          {
            mode: 'table',
            label: '表格页',
            showField: 'preAudit',
            rowSelection: {
              selectedRowKeys,
              onChange: (newSelectedRowKeys: React.Key[], newSelectedRows: MergedOrder[]) => {
                setSelectedRowKeys(newSelectedRowKeys);
                setSelectedRows(newSelectedRows);
              },
            },
            renderActions: renderTableActions,
          },
          {
            mode: 'list',
            label: '产品页',
            rowSelection: {
              selectedRowKeys,
              onChange: (newSelectedRowKeys: React.Key[], newSelectedRows: MergedOrder[]) => {
                setSelectedRowKeys(newSelectedRowKeys);
                setSelectedRows(newSelectedRows);
              },
            },
            renderActions: renderTableActions,
            renderInfo,
          },
        ]}
      />
      {isTransferModalVisible && (
        <TransferOrderModal
        visible={isTransferModalVisible}
        orders={selectedRows}
        actionType='transfer'
        onCancel={() => setIsTransferModalVisible(false)}
        onSuccess={() => {
          setIsTransferModalVisible(false);
          setSelectedRowKeys([]);
          setSelectedRows([]);
          setRefreshKey((k) => k + 1);
        }}
      />
      )}
    </OrderTriggerActionProvider>
  );
};

export default TeamAuditWorkbench;
